import * as turf from '@turf/turf';
import { useContext, useState } from 'react';
import { useParams } from 'react-router';

import { Button } from 'reactstrap';
import styled from 'styled-components';
import { MapContext } from '../../contexts/MapContext';
import {
  asDynamicDecimal,
  asQuantity,
  unitForValue,
} from '../../utils/formatters';
import { PanelMainGroup, PanelSection } from '../SidePanel';
import RightMenuTabs from './RightMenuTabs';
import { rightMenuTabs } from '../../contexts/RightMenuTabContext';
import { LabeledData } from './menu-components';
import {
  defaultLayerUnitSpec,
  filterFeaturesByType,
  getCustomLayerColor,
  getLayerArea,
  getLayerLength,
  layerFeatureIdFromGeojsonFeatureId,
  layerIsCustom,
} from '../../utils/layers';
import CustomLayerInspectSections from './CustomLayerInspectSections';
import { ColorDropdown } from './ColorDropdown';
import { COLOR_OPTIONS } from '../../constants/colors';
import LayerGeometrySection from './LayerGeometrySection';
import LayerFeatureNotes from './notes/LayerFeatureNotes';
import LayerNotes from './notes/LayerNotes';
import { captureEvent } from '../../utils/analytics';
import LayerExportButtons from './export/LayerExportButtons';
import { useFarmName } from '../../utils/hooks';
import { SOIL_BOUNDARIES_LAYER_ID } from '../../constants/map';
import SoilLayerMenu from './SoilLayerMenu';

const UnderlinedButton = styled(Button)`
  color: var(--text-color);
  text-decoration: underline;
  &:hover {
    color: var(--text-muted-color);
    text-decoration: underline;
  }
`;

/**
 * NOTE: `featureId` = geojson feature id
 */
const LayerMenu = ({ layerId, featureId, readonly }) => {
  const [activeTab, setActiveTab] = useState(rightMenuTabs.DESIGN);
  const { farmId } = useParams();
  const farmName = useFarmName(!farmId);
  const {
    layers: { layers, setCustomLayerColor, setLayerShown },
    mapState: { activeLayerFeature, selectedLayerFeatures },
  } = useContext(MapContext);

  const layer = layers[layerId];
  const { data, drawTool, hasRemote, loadSkipped, shown, units, width } = layer;
  const isCustom = layerIsCustom(layer);
  const color = getCustomLayerColor(layer);
  const isSoil = layerId === SOIL_BOUNDARIES_LAYER_ID;

  const setLayerColor = async (newColor) => {
    setCustomLayerColor({ layer, farmId, color: newColor });
    captureEvent('custom-layer-set-color');
  };

  const activeFeatures = selectedLayerFeatures.length
    ? turf.featureCollection(selectedLayerFeatures)
    : activeLayerFeature
    ? turf.featureCollection([activeLayerFeature])
    : data;

  const polygons = filterFeaturesByType(activeFeatures, 'Polygon');
  const lines = filterFeaturesByType(activeFeatures, 'LineString');
  const points = filterFeaturesByType(activeFeatures, 'Point');

  const area = getLayerArea(layer, polygons);
  const length = getLayerLength(layer, lines);

  const activeLayerFeatureRecordId =
    featureId && layerFeatureIdFromGeojsonFeatureId(featureId);

  if (!data)
    return (
      <PanelSection>
        {hasRemote && !shown ? (
          <div className="d-flex align-items-center">
            <UnderlinedButton
              className="btn-sm p-0"
              color="link"
              onClick={() => setLayerShown({ layerId })}
            >
              Show
            </UnderlinedButton>
            <span>&nbsp;layer to load data.</span>
          </div>
        ) : hasRemote && loadSkipped ? (
          'Loading...'
        ) : (
          `No data. Import ${
            drawTool ? 'or draw' : ''
          } layer data to get started.`
        )}
      </PanelSection>
    );

  const featureCount = selectedLayerFeatures.length || data.features?.length;

  const hasInspectSection =
    featureId == null /* Show quantity */ ||
    area != null ||
    length != null ||
    points.features.length > 1;

  const hasColorDropdown =
    isCustom && featureId == null && !selectedLayerFeatures.length;
  const isPointFeature = activeLayerFeature?.geometry?.type === 'Point';
  const hasGeometrySection = width != null || isPointFeature;
  const hasDesignSection = hasGeometrySection || hasColorDropdown;
  const showTabs = hasInspectSection && hasDesignSection && !readonly;
  const showDesignSection =
    hasDesignSection && (!showTabs || activeTab === rightMenuTabs.DESIGN);
  const showInspectSection =
    hasInspectSection && (!showTabs || activeTab === rightMenuTabs.INSPECT);

  const inspectMainContent = (
    <>
      <h4>
        {selectedLayerFeatures.length
          ? 'Features'
          : featureId
          ? 'Feature'
          : 'Layer'}
      </h4>
      {(selectedLayerFeatures.length || featureId == null) && (
        <LabeledData
          copyable
          label="qty"
          unit={unitForValue('features', 'feature')}
          formatter={asQuantity}
        >
          {featureCount}
        </LabeledData>
      )}
    </>
  );

  const areaStatsContent =
    area == null ? null : (
      <LabeledData
        copyable
        label="area"
        formatter={asDynamicDecimal}
        unitSpec={units?.unitSpec ?? defaultLayerUnitSpec.polygon}
      >
        {area}
      </LabeledData>
    );

  const lengthStatsContent =
    length == null ? null : (
      <LabeledData
        copyable
        label="length"
        formatter={asDynamicDecimal}
        unitSpec={units?.unitSpec ?? defaultLayerUnitSpec.lineString}
      >
        {length}
      </LabeledData>
    );

  return (
    <>
      {showTabs && (
        <RightMenuTabs activeTab={activeTab} setActiveTab={setActiveTab} />
      )}
      <PanelMainGroup>
        {showDesignSection && (
          <>
            {hasGeometrySection && (
              <LayerGeometrySection
                layerId={layerId}
                featureId={featureId}
                readonly={readonly}
              />
            )}

            {hasColorDropdown && !readonly && (
              <PanelSection>
                <h4>Settings</h4>

                <LabeledData label="color">
                  <ColorDropdown
                    options={COLOR_OPTIONS}
                    value={color}
                    onSelect={setLayerColor}
                    readonly={readonly}
                  />
                </LabeledData>
              </PanelSection>
            )}
          </>
        )}

        {isSoil && <SoilLayerMenu />}
        {showInspectSection &&
          !isSoil &&
          // For custom layers, we split the stats into sections by feature type
          (isCustom && featureId == null ? (
            <CustomLayerInspectSections
              inspectMainContent={inspectMainContent}
              areaStatsContent={areaStatsContent}
              lengthStatsContent={lengthStatsContent}
              polygonsCount={polygons.features.length}
              linesCount={lines.features.length}
              pointsCount={points.features.length}
            />
          ) : (
            // for non-custom layers, there's only one kind of stat:
            <PanelSection>
              {inspectMainContent}
              {areaStatsContent || lengthStatsContent}
            </PanelSection>
          ))}
        {!!featureCount && (
          <PanelSection>
            <h4>Export</h4>
            <LayerExportButtons
              className="w-100 text-truncate"
              color="secondary"
              layer={layer}
              outline
              size="sm"
              title={[layer.displayName, farmName].join(' - ')}
            >
              Export {layer.displayName}
            </LayerExportButtons>
          </PanelSection>
        )}
        {
          // When a LayerFeature is selected, show notes for that LayerFeature.
          // Otherwise, show all notes within the Layer:
        }
        {isCustom &&
          (activeLayerFeatureRecordId ? (
            <LayerFeatureNotes layerFeatureId={activeLayerFeatureRecordId} />
          ) : (
            <LayerNotes layerId={layerId} />
          ))}
      </PanelMainGroup>
    </>
  );
};

export default LayerMenu;
